import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Divider,
  Avatar,
  Menu,
  MenuItem,
  Container,
  ListItemButton,
  useTheme,
  Paper,
  Badge,
  Tooltip,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  ExitToApp as LogoutIcon,
  Description as FileIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  SupervisorAccount as SupervisorAccountIcon,
  MonitorHeartOutlined as MonitoringIcon,
} from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";

const drawerWidth = 280;

const Layout = ({ children }) => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const theme = useTheme();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleNavigation = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const navigationItems = [
    {
      text: "Meus Arquivos",
      icon: <DashboardIcon />,
      path: "/",
    },
    // Show groups and user management only for admins
    ...(currentUser?.role === "ADMIN"
      ? [
        {
          text: "Grupos",
          icon: <GroupIcon />,
          path: "/groups",
        },
        {
          text: "Gerenciar Usuários",
          icon: <SupervisorAccountIcon />,
          path: "/users/manage",
        },
        {
          text: "Monitoramento",
          icon: <MonitoringIcon />,
          path: "/monitoring",
        },
      ]
      : []),
  ];

  const drawer = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", borderRadius: "0" }}>
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <Box
          component="img"
          src="/vanirasystem.png"
          alt="Vanira System"
          sx={{
            height: 50,
            width: "auto",
          }}
        />
      </Box>
      <Divider />
      <List sx={{ px: 2, py: 2, flex: 1 }}>
        {navigationItems.map((item) => (
          <ListItemButton
            key={item.path}
            onClick={() => handleNavigation(item.path)}
            selected={location.pathname === item.path}
            sx={{
              borderRadius: 2,
              mb: 1,
              transition: "all 0.2s",
              "&.Mui-selected": {
                backgroundColor: "primary.light",
                color: "primary.main",
                "&:hover": {
                  backgroundColor: "primary.light",
                },
                "& .MuiListItemIcon-root": {
                  color: "primary.main",
                },
              },
              "&:hover": {
                backgroundColor: theme.palette.grey[100],
                transform: "translateX(4px)",
              },
            }}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText
              primary={item.text}
              primaryTypographyProps={{
                fontWeight: location.pathname === item.path ? 600 : 400,
              }}
            />
          </ListItemButton>
        ))}

        <Box sx={{ mt: 2 }}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ px: 2, fontSize: "0.75rem" }}
          >
            Recentes
          </Typography>
          <List sx={{ py: 1 }}>
            <ListItemButton
              sx={{
                borderRadius: 2,
                mb: 0.5,
                py: 1,
                "&:hover": {
                  backgroundColor: theme.palette.grey[100],
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <FileIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Documento.docx"
                primaryTypographyProps={{
                  variant: "body2",
                  noWrap: true,
                }}
              />
            </ListItemButton>
            <ListItemButton
              sx={{
                borderRadius: 2,
                mb: 0.5,
                py: 1,
                "&:hover": {
                  backgroundColor: theme.palette.grey[100],
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <FileIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Relatório.pdf"
                primaryTypographyProps={{
                  variant: "body2",
                  noWrap: true,
                }}
              />
            </ListItemButton>
          </List>
        </Box>
      </List>
      <Divider />
      <Box sx={{ p: 2 }}>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: "primary.light",
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            "&:hover": {
              bgcolor: "grey.100",
            },
          }}
          onClick={handleProfileMenuOpen}
        >
          <Avatar
            sx={{ width: 40, height: 40, bgcolor: "primary.main", mr: 1.5 }}
          >
            {currentUser?.name?.charAt(0) || "U"}
          </Avatar>
          <Box sx={{ flex: 1, overflow: "hidden" }}>
            <Typography variant="subtitle2" noWrap>
              {currentUser?.name || "Usuário"}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {currentUser?.email}
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "grey.50", }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          bgcolor: "background.paper",
          borderBottom: "1px solid",
          borderRadius: "0",
          borderColor: "divider",
          color: "text.primary",
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: "none" } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{
              flexGrow: 1,
              fontWeight: 600,
              color: "text.primary",
            }}
          >
            {location.pathname === "/"
              ? "Meus Arquivos"
              : location.pathname === "/users/manage"
                ? "Gerenciar Usuários"
                : location.pathname === "/groups"
                  ? "Meus Grupos"
                  : location.pathname === "/profile"
                    ? "Meu Perfil"
                    : location.pathname === "/monitoring"
                      ? "Monitoramento de Atividades"
                      : ""}
          </Typography>

          {/* <Box sx={{ display: "flex", alignItems: "center" }}>
            <Tooltip title="Configurações">
              <IconButton sx={{ mx: 1 }}>
                <SettingsIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Notificações">
              <IconButton sx={{ mx: 1 }}>
                <Badge badgeContent={3} color="primary">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Tooltip>
          </Box> */}
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              bgcolor: "background.paper",
              borderRight: "1px solid",
              borderColor: "divider",
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              bgcolor: "background.paper",
              borderRight: "1px solid",
              borderColor: "divider",
              borderRadius: "0"
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          minHeight: "100vh",
          bgcolor: "grey.50",
        }}
      >
        <Toolbar />
        <Container maxWidth="xl" sx={{ py: 2 }}>
          {children}
        </Container>
      </Box>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        keepMounted
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 1.5,
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.1))",
            "& .MuiMenuItem-root": {
              px: 2,
              py: 1,
            },
          },
        }}
      >
        <MenuItem
          onClick={() => {
            handleProfileMenuClose();
            navigate("/profile");
          }}
        >
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Meu Perfil" />
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Sair" />
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Layout;
